import { useAuth0 } from '@auth0/auth0-react';
import { createAnalyticsState } from 'components/Dashboard/Analytics/createAnalyticsState';
import { compileData } from 'components/Dashboard/Analytics/MentionsByTopicContainer';
import MentionsRightPanel from 'components/Dashboard/Analytics/MentionsRightPanel';
import Preloader from 'components/Dashboard/Analytics/ui/AnalyticsDashboard/Preloader';
import DownloadChartModal from 'components/Dashboard/Analytics/ui/DownloadChartModal/DownloadChartModal';
import { useCompileMentionsDataFunctions } from 'components/Dashboard/Analytics/ui/MentionsByTopic';
import FiltersBar from 'components/Dashboard/Analytics/ui/AnalyticsFilters/FiltersBar';
import MentionsChart from 'components/Dashboard/Analytics/ui/MentionsByTopic/MentionsChart';
import useCallMentionsByTopic from 'components/Dashboard/Analytics/useCallMentionsByTopic';
import { searchIncludesTitleConditional } from 'components/Dashboard/StakeHolders/searchIncludesTitle';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { store } from 'components/Store';
import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

const StakeholderMentionsOverTime = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists, activePlan } = state;
  const { enableTwitter } = activePlan ?? {};
  const { StakeholderInformation, scrollBarRef } = props;
  const { name, officalName, categories } = StakeholderInformation;
  const specialAdviser = categories?.includes('Special Adviser');

  const [stakeholderAnalyticsState, setStakeholderAnalyticsState] = useState({
    ...createAnalyticsState(),
    chartDataOptionSelected: enableTwitter ? 'Contribution type' : 'Overall',
  });
  const [chartKey, setChartKey] = useState('');
  const [compiledData, setCompiledData] = useState([]);
  const { currentView, activeDateInterval } = stakeholderAnalyticsState ?? {};
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({});
  const [rightPanelState, setRightPanelState] = useState({ open: false });
  const [rawData, setRawData] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(null);

  const chartItem = useRef(null);
  const chartSection = useRef(null);
  const mentionsDataSection = useRef(null);
  const downloadButton = useRef(null);

  const { callMentionsByTopic } = useCallMentionsByTopic({ chartType: 'Mentions' });

  useEffect(() => {
    if (chartItem?.current) {
      setCompiledData(compileTotalsWithFilters());
    }
  }, [chartKey, selectedCategories]);

  const { compileTotalsWithFilters, createSectionValues } = useCompileMentionsDataFunctions({
    chartRef: chartItem,
    data,
    analyticsState: stakeholderAnalyticsState,
    rawData,
  });
  const { isAuthenticated } = useAuth0();

  const topicParameter = getUrlParam('topic-id');
  const searchParameter = getUrlParam('search');
  const topic = keywordsLists?.find((item) =>
    topicParameter === 'all' ? item?.id === null : item?.id === parseInt(topicParameter)
  );
  const noSearchCondition =
    topicParameter === 'all' ||
    searchIncludesTitleConditional(officalName) ||
    (!topicParameter && !searchParameter) ||
    specialAdviser ||
    !isAuthenticated;
  const isMobile = useIsMobile();

  const params = useParams();
  const { id } = params;
  const initialCall = useRef(null);
  initialCall.current = async ({ source }) => {
    if (!noSearchCondition) {
      try {
        setIsLoading(true);
        setRightPanelState({ ...rightPanelState, open: false });
        setChartKey('');
        setRightPanelState({ open: false });
        let options = {
          view: currentView,
          dateInterval: activeDateInterval?.filter,
          freeSearch: searchParameter,
          stakeholderId: id,
          source,
        };
        if (enableTwitter) {
          options = {
            ...options,
            chartDataOption: 'Contribution type',
            includeTweetsOnMentions: true,
          };
        }
        const results = await callMentionsByTopic(options);
        if (!!results) {
          setData(results?.data);
          if (results?.rawData) {
            setRawData(results.rawData);
          }
          setTimeout(() => {
            setChartKey('Mentions');
            setIsLoading(false);
          }, 300);
        }
      } catch (error) {}
    }
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current({ source });
    return () => {
      source.abort('Stakeholder mentions loading cancelled by the user');
    };
  }, [topicParameter, searchParameter, currentView, activeDateInterval]);

  const emptyMessage = data.labels?.length === 0;
  const modifyFiltersBarFunction = ({ property, newValue }) => {
    const newState = property === 'CompleteState' ? newValue : { ...stakeholderAnalyticsState, [property]: newValue };
    setStakeholderAnalyticsState(newState);
  };

  const compiledDataRaw = data?.value ? compileData(data, stakeholderAnalyticsState)?.raw : [];
  const createDatesLabels = () => {
    return compiledDataRaw?.length > 0 ? compiledDataRaw?.map((item) => item.date) : [];
  };
  const labelDates = createDatesLabels();

  return (
    <>
      {noSearchCondition ? (
        <p
          className='text-center title-h5 light-70-text mx-auto no-activity-message'
          aria-label='To see interest over time, select a specific topic or do a keyword search in the dropdown above'
        >
          {specialAdviser ? (
            'Interest over time is not available for Special Advisers'
          ) : (
            <>
              To see interest over time, select a <span className='font-weight-bold'>specific topic</span> <br />
              or do a <span className='font-weight-bold'>keyword search</span> in the dropdown above
            </>
          )}
        </p>
      ) : (
        <div className='px-4 px-lg-5 pb-4 pt-2'>
          <h3 className='category-subtitle mb-0 line-height-1-5 '>
            Political contributions in relation to: {searchParameter ?? topic?.name}
          </h3>
          <div className='rounded border p-4'>
            <div className='mb-4 stakeholder-sentiment-filters-bar flex-centered'>
              <div className='flex-grow-1 overflow-hidden pr-3 pr-lg-0 stakeholder-sentiment-filters-container'>
                <div className='w-100 overflow-auto '>
                  <FiltersBar
                    typeOfChartSections={true}
                    chartName={'Mentions'}
                    analyticsState={stakeholderAnalyticsState}
                    modifyFiltersBar={modifyFiltersBarFunction}
                  />
                </div>
              </div>
              <button
                ref={downloadButton}
                className='general-button circle-button circle-button-36 border'
                disabled={isLoading}
                onClick={() => {
                  downloadButton.current.blur();
                  setShowDownloadModal(true);
                }}
                data-tooltip-id={'general-tooltip'}
                data-tooltip-content={'Download chart'}
              >
                <span className='icon-download' />
              </button>
            </div>
            {isLoading ? (
              <Preloader />
            ) : (
              <div ref={chartSection} style={{ minHeight: '250px' }}>
                <MentionsChart
                  key={`chart-ref-${stakeholderAnalyticsState?.partySelectedCategories?.length}`}
                  ref={chartItem}
                  data={data}
                  compiledData={compiledData}
                  analyticsState={stakeholderAnalyticsState}
                  emptyMessage={emptyMessage}
                  customGraph={true}
                  isLoading={isLoading}
                  setRightPanelState={setRightPanelState}
                  selectedCategories={selectedCategories}
                  setSelectedCategories={setSelectedCategories}
                  setActiveSection={setActiveSection}
                  activeSection={activeSection}
                  labelDates={labelDates}
                  stakeholderName={name}
                  showRightPanelOnClick={true}
                />
              </div>
            )}
          </div>

          {!isLoading && rightPanelState.open && !emptyMessage && (
            <div ref={mentionsDataSection}>
              <MentionsRightPanel
                contentState={rightPanelState}
                setRightPanelState={setRightPanelState}
                analyticsState={stakeholderAnalyticsState}
                createSectionValues={createSectionValues}
                stakeholderId={id}
                afterLoadingResults={() => {
                  const offsetValue = isMobile ? 0 : 170;
                  setTimeout(() => {
                    scrollBarRef?.current?.view?.scrollTo({
                      top: mentionsDataSection.current?.offsetTop - offsetValue,
                      behavior: 'smooth',
                    });
                  }, 300);
                }}
              />
            </div>
          )}
        </div>
      )}
      <DownloadChartModal
        setIsOpen={setShowDownloadModal}
        isOpen={showDownloadModal}
        chartSection={chartSection}
        type={'Mentions'}
        data={data}
        chartRef={chartItem}
        analyticsState={stakeholderAnalyticsState}
        setChartOnDownload={() => {}}
        initialHeader={`Political mentions by ${name}: ${searchParameter ?? topic?.name}`}
        stakeholderPage={true}
      />
    </>
  );
};

export default StakeholderMentionsOverTime;
